import { useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';

import DeleteConversationDialog from '@/components/shared/AgentAssistantPopup/DeleteConversationDialog';
import { usePopupView } from '@/components/shared/AgentAssistantPopup/PopupViewContext';
import { groupSessionsByDate } from '@/components/shared/AgentAssistantPopup/groupSessionsByDate';
import { ConversationHistoryRow } from '@/components/shared/AgentAssistantPopup/slots/ConversationHistoryRow';
import {
  Centered,
  GroupLabel,
  HistoryRoot,
} from '@/components/shared/AgentAssistantPopup/styles';
import type { ChatSession } from '@/hooks/useAgentChatSessions';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';

const UNTITLED = 'New conversation';

export const ConversationHistoryList = () => {
  const { history, setView } = usePopupView();
  const [pendingDelete, setPendingDelete] = useState<ChatSession | null>(null);
  const snackbar = useSnackbar();

  if (!history) return null;

  const {
    sessions,
    isLoading,
    error,
    currentThreadId,
    onSelectThread,
    onRenameThread,
    onDeleteThread,
  } = history;

  if (isLoading && sessions.length === 0) {
    return (
      <HistoryRoot>
        <Centered>
          <CircularProgress size={24} />
        </Centered>
      </HistoryRoot>
    );
  }

  if (error && sessions.length === 0) {
    return (
      <HistoryRoot>
        <Centered>
          <Typography variant="body2" color="text.secondary">
            Couldn&apos;t load conversations. Please try again.
          </Typography>
        </Centered>
      </HistoryRoot>
    );
  }

  if (sessions.length === 0) {
    return (
      <HistoryRoot>
        <Centered>
          <Typography variant="body2" color="text.secondary">
            No previous conversations
          </Typography>
        </Centered>
      </HistoryRoot>
    );
  }

  const groups = groupSessionsByDate(sessions);

  const handleSelect = (sessionId: string) => {
    onSelectThread(sessionId);
    setView('chat');
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await onDeleteThread?.(pendingDelete.sessionId);
    } catch {
      snackbar({
        message: "Couldn't delete conversation. Please try again.",
        severity: 'error',
      });
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <HistoryRoot>
      {groups.map((group) => (
        <Box key={group.label}>
          <GroupLabel>{group.label}</GroupLabel>
          {group.sessions.map((session) => (
            <ConversationHistoryRow
              key={session.sessionId}
              session={session}
              active={session.sessionId === currentThreadId}
              onSelect={handleSelect}
              onRequestDelete={setPendingDelete}
              onRename={onRenameThread}
            />
          ))}
        </Box>
      ))}
      <DeleteConversationDialog
        open={pendingDelete !== null}
        conversationName={pendingDelete?.chatName ?? UNTITLED}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </HistoryRoot>
  );
};
